import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Levenshtein distance in Haskell",
  "description": "",
  "published": "2012-07-06T00:00:00.000Z",
  "redirect_from": ["/blog/levenshtein-distance-in-haskell/swizec/4801"],
  "categories": "Algorithm, Dynamic programming, Edit distance, Haskell, Languages, Levenshtein distance, Programming, String searching algorithm, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`When you have to figure out the difference between two strings your best bet is either H`}<em parentName="p">{`amming distance`}</em>{` or `}<em parentName="p"><a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/Levenshtein_distance",
          "title": "Levenshtein distance"
        }}>{`Levenshtein distance`}</a></em>{`.`}</p>
    <p>{`Because `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Hamming_distance",
        "title": "Hamming distance"
      }}>{`hamming distance`}</a>{` only works on strings of equal lengths, you're usually better off using levenshtein distance. Its name is also harder to spell so you know it's better!`}</p>
    <p>{`It is defined as `}<em parentName="p">{`the minimum amount of edits needed to transform one string into another`}</em>{`. Like this:`}</p>
    <blockquote>
      <ol parentName="blockquote">
        <li parentName="ol"><strong parentName="li">{`k`}</strong>{`itten → `}<strong parentName="li">{`s`}</strong>{`itten (substitution of 's' for 'k')`}</li>
        <li parentName="ol">{`sitt`}<strong parentName="li">{`e`}</strong>{`n → sitt`}<strong parentName="li">{`i`}</strong>{`n (substitution of 'i' for 'e')`}</li>
        <li parentName="ol">{`sittin → sittin`}<strong parentName="li">{`g`}</strong>{` (insertion of 'g' at the end).`}</li>
      </ol>
    </blockquote>
    <p>{`Levenshtein distance also happens to be a great case for learning about dynamic programming, because one of the simplest implementations involves nothing more than keeping track of the already known distances and deciding whether you should increase that number for the next character.`}</p>
    <p>{`Doing this in a 2D array is trivially simple because it lest you travel through the distance space very efficiently without forgetting that `}<em parentName="p">{`kitten`}</em>{` and `}<em parentName="p">{`kiten`}</em>{` only have a levenshtein distance of 1.`}</p>
    <p>{`The meat of the algorithm goes like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-pseudo"
      }}>{`      if s[i] = t[j] then
        d[i, j] := d[i-1, j-1]       // no operation required
      else
        d[i, j] := minimum
                   (
                     d[i-1, j] + 1,  // a deletion
                     d[i, j-1] + 1,  // an insertion
                     d[i-1, j-1] + 1 // a substitution
                   )
`}</code></pre>
    <h2 {...{
      "id": "dynamic-programming-and-haskell"
    }}><a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Dynamic_programming",
        "title": "Dynamic programming"
      }}>{`Dynamic programming`}</a>{` and Haskell`}</h2>
    <p>{`Doing this in Haskell becomes tricky because we don't have state so we can't really keep track of what we already know. Translating this into a recursive problem is our only option.`}</p>
    <p>{`The key insight comes from looking at what those index manipulations are doing - traveling in the space of two strings.`}</p>
    <p>{`So, if we start from the other end of the "matrix" - bottom right instead of top left - we can express the levenshtein distance recursively:`}</p>
    <ul>
      <li parentName="ul">{`if last two characters are same, ignore them`}</li>
      <li parentName="ul">{`otherwise take the minimum of ignoring either or both the last characters`}</li>
      <li parentName="ul">{`recurse`}</li>
    </ul>
    <p>{`In Haskell the function ends up looking like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`-- calculate levenshtein distance between two strings
levenshtein::[Char] -> [Char] -> Int
levenshtein "" "" = 0
levenshtein "" s2 = length s2
levenshtein s1 "" = length s1
levenshtein s1 s2
   | last s1 == last s2 = levenshtein (init s1) (init s2)
   | otherwise = minimum [1 + levenshtein (init s1) s2,
                          1 + levenshtein s1 (init s2),
                          1 + levenshtein (init s1) (init s2)]
`}</code></pre>
    <p>{`Better Haskellers than me can probably write this a bit cleaner - for instance you don't need the `}<em parentName="p">{`levenshtein "" "" = 0`}</em>{` line, but I think it's more readable when all the border cases are spelled out explicitly.`}</p>
    <p>{`However, the problem is that this is incredibly slow when comparing a long string to a short one. So slow in fact, I never waited for the code to stop executing (takes 20s+).`}</p>
    <p>{`Luckily, speeding up the code is trivial - look at those border cases. We can just handle those first and `}<em parentName="p">{`then`}</em>{` go into the meaty algorithm!`}</p>
    <p>{`The code becomes somewhat uglier to look at, but is usefully quick for all cases:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`-- calculate levenshtein distance between two strings
levenshtein::[Char] -> [Char] -> Int
-- this part is mostly a speed optimiziation
levenshtein s1 s2
  | length s1 > length s2 = levenshtein s2 s1
  | length s1 < length s2 =
    let d = length s2 - length s1
    in d + levenshtein s1 (take (length s2 - d) s2)
-- the meat of the algorithm
levenshtein "" "" = 0
levenshtein s1 s2
  | last s1 == last s2 = levenshtein (init s1) (init s2)
  | otherwise = minimum [1 + levenshtein (init s1) s2,
                         1 + levenshtein s1 (init s2),
                         1 + levenshtein (init s1) (init s2)]
`}</code></pre>
    <p>{`If anyone's got an even better idea for implementing Levenshtein distance in Haskell, I'd love to hear!`}</p>
    <p>{`edit: A Reddit commenter provides a far better `}<a parentName="p" {...{
        "href": "http://www.reddit.com/r/programming/comments/w4gs6/levenshtein_distance_in_haskell/c5a6jjz"
      }}>{`Levenshtein distance function`}</a>{`.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.jefclaes.be/2012/01/autocorrecting-unknown-actions-using.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/87510074_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.jefclaes.be/2012/01/autocorrecting-unknown-actions-using.html"
        }}>{`Autocorrecting unknown actions using the Levenshtein distance`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/5859561/getting-the-closest-string-match"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/87174255_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/5859561/getting-the-closest-string-match"
        }}>{`Incredibly thorough response to a Fuzzy String Search question on SO`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://cs.stackexchange.com/questions/2093/efficient-map-data-structure-supporting-approximate-lookup"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/91010017_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://cs.stackexchange.com/questions/2093/efficient-map-data-structure-supporting-approximate-lookup"
        }}>{`Efficient map data structure supporting approximate lookup`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/5859561/getting-the-closest-string-match/5859691"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/87179321_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/5859561/getting-the-closest-string-match/5859691"
        }}>{`Answer by adorablepuppy for Getting the closest string match`}</a></li>
    </ul>
    <hr></hr>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=350fa2c4-f571-457e-a891-22f73c43d8a7",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      